import {Component, OnDestroy, OnInit} from '@angular/core';
import {RecommendationManagerService} from '../recommendation-manager.service';
import {NavBarComponent} from '../../nav-bar/nav-bar.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {RecommendationManagerSharedDataService} from '../recommendation-manager-shared-data.service';
import {MatDialog} from '@angular/material/dialog';
import {DraftConfirmationDialogComponent} from '../../dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component';
import {CustomerModel} from '../../order-to-cash/models-otc/customer-model';
import {MatTableDataSource} from '@angular/material/table';
import {PageEvent} from '@angular/material/paginator';
import { filenameFromDisposition } from '../../shared/content-disposition';

export interface VitaReportResponse {
  sampleId: number;
  cropId: number;
  cultivarId: number;
  regionId: number;
  stageId: number;
};

@Component({
    selector: 'app-recommendation-details',
    templateUrl: './recommendation-details.component.html',
    styleUrls: ['./recommendation-details.component.css'],
    standalone: false
})
export class RecommendationDetailsComponent implements OnInit, OnDestroy {

  recommendationId: string = '';
  subscriptions: Subscription[] = [];
  recommendationDetails: any = null;
  currentPage: number = 0;
  pageSize: number = 25;
  searchText: string = '';
  comment: string = '';
  note: string = '';
  timerInterval: any;
  panelOpenState = false;
  showDownloadsMenu = false;
  vitaReportResponse: VitaReportResponse[] = [];

  constructor(
      private recommendationManagerService: RecommendationManagerService,
      private nav: NavBarComponent,
      private route: ActivatedRoute,
      private recommendationManagerSharedDataService: RecommendationManagerSharedDataService,
      private dialog: MatDialog,
      private router: Router
  ) {
  }

  ngOnInit() {
    this.recommendationId = this.route.snapshot.paramMap.get('id');
    this.getRecommendationDetails();
    this.getSoftTissueForRecommendation();
  }

  saveRecommendationDraft() {
    this.nav.toggleLoading(true);
    this.subscriptions = [...this.subscriptions,
        this.recommendationManagerService.saveRecommendationDraft(this.recommendationId, this.note, this.comment).subscribe({
          next: () => {
            this.nav.toggleLoading(false);
          },
          error: () => {
            this.nav.toggleLoading(false);
          }
        })
    ]
  }

  getRecommendationDetails() {
    this.subscriptions = [...this.subscriptions,
      this.recommendationManagerService.getRecommendationDetails(this.recommendationId).subscribe({
        next: res => {
          this.recommendationDetails = res;
          this.recommendationManagerSharedDataService.updateRecommendationDetails(res);
        }
      })
    ];
  }

  getSoftTissueForRecommendation() {
    if(this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.subscriptions = [...this.subscriptions,
      this.recommendationManagerService.getSoftTissueForRecommendation(this.recommendationId, this.searchText, this.currentPage, this.pageSize).subscribe({
        next: (res: any) => {
          // console.log(res)

          if(res?.content?.length > 0) {
            for(let softTissueObj of res.content) {
              this.vitaReportResponse.push({
                sampleId: softTissueObj?.pinSamples?.id,
                cropId: softTissueObj?.crop?.id,
                cultivarId: null,
                regionId: null,
                stageId: null
              });
            }
          }

          this.recommendationManagerSharedDataService.updateRecommendationSoftTissueList(res);
        }
      })
    ];
  }

  onPaginateChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getSoftTissueForRecommendation();
  }

  openDeleteDraftDialog() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'delete',
        dialogTitle: 'DELETE DRAFT',
        dialogSubTitle: 'Are you sure you want to delete this Leaf Interpretation?'
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        this.nav.toggleLoading(true);
        this.recommendationManagerService.deleteRecommendation(this.recommendationId).subscribe({
          next: () => {
            this.nav.toggleLoading(false);
            this.router.navigateByUrl('recommendation-manager');
            this.recommendationManagerSharedDataService.updateRecommendationDetails(null);
            this.recommendationManagerSharedDataService.updateRecommendationSoftTissueList(null);
          }
        })
      }
    })
  }

  searchRecommendations() {
    if(this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      this.getSoftTissueForRecommendation();
    }, 800);
  }

  downloadSoftTissueReport() {
    this.nav.toggleLoading(true);
    this.subscriptions = [...this.subscriptions,
        this.recommendationManagerService.downloadSoftTissueReport(this.recommendationId).subscribe({
          next: (res) => {
            let data = res.body

            if (data) {
              const url = window.URL.createObjectURL(data);
              const link = document.createElement('a');
              link.href = url;

              // Was String(header), which only ever worked because the server sent a bare file
              // name instead of a real Content-Disposition. It sends a correct one now.
              link.setAttribute('download', filenameFromDisposition(
                res.headers.get('content-disposition'), `recommendation-${this.recommendationId}.pdf`));

              document.body.appendChild(link);
              link.click();
              link.parentNode.removeChild(link);
            }

            this.nav.toggleLoading(false);
          },
          error: () => {
            this.nav.toggleLoading(false);
          }
        })
    ]
  }

  downloadVitaReport() {
    this.nav.toggleLoading(true);
    this.subscriptions = [...this.subscriptions,
        this.recommendationManagerService.downloadVitaReport(this.recommendationId, this.vitaReportResponse).subscribe({
          next: (res) => {
            let data = res.body

            if (data) {
              const url = window.URL.createObjectURL(data);
              const link = document.createElement('a');
              link.href = url;

              // Was String(header) here too. The Vita endpoint sent a bare file name rather
              // than a real Content-Disposition, and the two faults cancelled out.
              link.setAttribute('download',
                  filenameFromDisposition(res.headers.get('content-disposition'),
                      `vita-report-${this.recommendationId}.pdf`));

              document.body.appendChild(link);
              link.click();
              link.parentNode.removeChild(link);
            }

            this.nav.toggleLoading(false);
          },
          error: () => {
            this.nav.toggleLoading(false);
          }
        })
    ]
  }

  backToRecommendations() {
    const dialogRef = this.dialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'back',
        dialogTitle: 'GO BACK?',
        dialogSubTitle: 'Unsaved changes will be lost, are you sure?'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.recommendationManagerSharedDataService.updateRecommendationDetails(null);
        this.recommendationManagerSharedDataService.updateRecommendationSoftTissueList(null);
        this.router.navigateByUrl('recommendation-manager/recommendations');
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => {
      s.unsubscribe();
    })
  }

}
